import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, FileArrowUp, Pencil, Trash, ArrowLeft, UserPlus, MagnifyingGlass,
  Gear, LockKey, XCircle, ClockCounterClockwise, HandCoins,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CreatorDialog from "@/components/CampaignDialog";
import PaymentDialog from "@/components/PaymentDialog";
import EditCampaignDialog from "@/components/EditCampaignDialog";
import AdditionalCostDialog from "@/components/AdditionalCostDialog";
import RazorpayPayoutDialog from "@/components/RazorpayPayoutDialog";
import CampaignActivityDrawer from "@/components/CampaignActivityDrawer";
import RequestCostPaymentDialog from "@/components/RequestCostPaymentDialog";
import { PaymentStatusBadge } from "@/components/StatusBadge";
import CsvImport from "@/components/CsvImport";
import MetricsImport from "@/components/MetricsImport";
import { useAuth } from "@/context/AuthContext";
import { fmtInt, fmtCur } from "@/components/Campaign/utils";
import { SummaryStat } from "@/components/Campaign/SummaryStat";
import { SheetLinks } from "@/components/Campaign/SheetLinks";
import { ReachStat } from "@/components/Campaign/ReachStat";
import { ProspectTable } from "@/components/Campaign/ProspectTable";
import { LockedTable } from "@/components/Campaign/LockedTable";
import { LiveTable } from "@/components/Campaign/LiveTable";
import { useInvoiceViewer } from "@/components/InvoiceViewer";

export default function CampaignDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { openInvoice } = useInvoiceViewer();
  const [campaign, setCampaign] = useState(null);
  const [creators, setCreators] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payCreator, setPayCreator] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [tab, setTab] = useState("prospect");
  const [editCampaignOpen, setEditCampaignOpen] = useState(false);
  const [deleteCampaignOpen, setDeleteCampaignOpen] = useState(false);
  const [addCosts, setAddCosts] = useState([]);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [costToDelete, setCostToDelete] = useState(null);
  const [razorpayOpen, setRazorpayOpen] = useState(false);
  const [razorpayCreator, setRazorpayCreator] = useState(null);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [costRequestFor, setCostRequestFor] = useState(null);
  const openRazorpay = (c) => { setRazorpayCreator(c); setRazorpayOpen(true); };

  const loadAddCosts = async () => {
    try {
      const { data } = await api.get(`/campaigns/${cid}/additional-costs`);
      setAddCosts(data);
    } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: crs }, prReq] = await Promise.all([
        api.get(`/campaigns/${cid}`),
        api.get(`/campaigns/${cid}/creators`),
        api.get(`/campaigns/${cid}/payment-requests`).catch(() => ({ data: [] })),
      ]);
      setCampaign(c);
      setCreators(crs);
      setPaymentRequests(Array.isArray(prReq.data) ? prReq.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (cid) loadAddCosts(); /* eslint-disable-next-line */ }, [cid]);

  // Deep-link: ?creator=<id> auto-opens that creator's dialog and switches
  // to the tab that matches its status. If the creator isn't in this
  // campaign (e.g. deleted), strip the param and surface a toast.
  useEffect(() => {
    const target = searchParams.get("creator");
    if (!target || !creators.length) return;
    const next = new URLSearchParams(searchParams);
    next.delete("creator");
    const c = creators.find((x) => x.id === target);
    if (!c) {
      toast.info("Creator not found in this campaign");
      setSearchParams(next, { replace: true });
      return;
    }
    const statusToTab = {
      "Prospect": "prospect",
      "Shortlisted": "shortlisted",
      "Locked": "locked",
      "Live": "live",
      "Cancelled": "cancelled",
    };
    const tabKey = (c.script_status === "Live") ? "live" : (statusToTab[c.status] || "prospect");
    setTab(tabKey);
    setEditing(c);
    setDialogOpen(true);
    setSearchParams(next, { replace: true });
  }, [creators, searchParams, setSearchParams]);

  const filtered = (list) => {
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((c) =>
      (c.username || "").toLowerCase().includes(s) ||
      (c.city || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s)
    );
  };

  const prospects = filtered(creators.filter((c) => c.status === "Prospect"));
  const shortlisted = filtered(creators.filter((c) => c.status === "Shortlisted"));
  const live = filtered(creators.filter((c) => c.status === "Live" || c.script_status === "Live"));
  const locked = filtered(creators.filter((c) => c.status === "Locked" && c.script_status !== "Live"));
  const cancelled = filtered(creators.filter((c) => c.status === "Cancelled"));

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/creators/${toDelete.id}`);
      toast.success(`Removed @${toDelete.username}`);
      setToDelete(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const openPayments = (creator) => { setPayCreator(creator); setPayOpen(true); };

  const addCostsTotal = addCosts.reduce((s, x) => s + Number(x.amount || 0), 0);
  const addCostsPaid = addCosts.reduce((s, x) => s + Number(x.paid_amount || 0), 0);
  const liveCount = creators.filter((c) => c.script_status === "Live").length;
  const sharedCount = creators.filter((c) => !!(c.live_link && String(c.live_link).trim())).length;
  const denom = (campaign?.no_of_creators) || creators.length;
  const totals = {
    creators: creators.length,
    locked: locked.length,
    live: liveCount,
    shared: sharedCount,
    allocated: creators.filter((c) => c.status === "Locked" || c.status === "Live").reduce((s, c) => s + Number(c.final_cost || 0), 0) + addCostsTotal,
    paid: creators.reduce((s, c) => s + Number(c.total_paid || 0), 0) + addCostsPaid,
  };

  const isLockedOrLive = (c) => c.status === "Locked" || c.status === "Live";
  const scopedCreators = creators.filter(isLockedOrLive);
  const internalCostTotal = scopedCreators.reduce((s, c) => s + Number(c.internal_cost || 0), 0);
  const clientCostTotal = scopedCreators.reduce((s, c) => s + Number(c.client_cost || 0), 0);
  const scopedPaid = scopedCreators.reduce((s, c) => s + Number(c.total_paid || 0), 0);
  const isCreatorBased = campaign?.campaign_type === "Creator Based";
  const scopedCount = scopedCreators.length;
  const agencyFeePct = Number(campaign?.agency_fee_pct || 0);
  const agencyFeeAmount = isCreatorBased ? clientCostTotal * (agencyFeePct / 100) : 0;
  const revenueDisplay = isCreatorBased ? clientCostTotal + agencyFeeAmount : Number(campaign?.commercial || 0);
  const allocatedDisplay = (isCreatorBased ? internalCostTotal : totals.allocated - addCostsTotal) + addCostsTotal;
  const paidDisplay = (isCreatorBased ? scopedPaid : totals.paid - addCostsPaid) + addCostsPaid;
  const pendingDisplay = Math.max(allocatedDisplay - paidDisplay, 0);
  const profitDisplay = isCreatorBased ? (clientCostTotal + agencyFeeAmount - internalCostTotal - addCostsTotal) : 0;

  if (loading || !campaign) {
    return <div className="text-slate-500 text-sm" data-testid="detail-loading">Loading campaign…</div>;
  }

  const onCreatorUpdated = (saved) => saved && setCreators((cs) => cs.map((c) => c.id === saved.id ? { ...c, ...saved } : c));

  return (
    <div className="space-y-5" data-testid="campaign-detail-page">
      <button
        onClick={() => navigate("/campaigns")}
        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="back-to-campaigns"
      >
        <ArrowLeft size={12} /> Back to campaigns
      </button>

      {/* Brief */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Campaign</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 break-words">{campaign.brand_name}</h1>
          {campaign.campaign_name && (
            <div className="text-xs sm:text-sm text-slate-600 mt-1">
              <span className="text-slate-400 uppercase text-[10px] tracking-wider mr-2">Campaign</span>
              {campaign.campaign_name}
            </div>
          )}
          <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-2xl">{campaign.objective || "No objective set yet."}</p>
          <SheetLinks label="Internal Sheets" items={campaign.internal_sheets} testidPrefix="campaign-internal-sheet" />
          <SheetLinks label="Client Sheets" items={campaign.client_sheets} testidPrefix="campaign-client-sheet" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setActivityOpen(true)} className="border-slate-300 h-9 px-3" data-testid="activity-btn">
            <ClockCounterClockwise size={16} className="mr-1.5" /> <span className="hidden sm:inline">Activity</span>
          </Button>
          <Button variant="outline" onClick={() => setEditCampaignOpen(true)} className="border-slate-300 h-9 px-3" data-testid="edit-campaign-btn">
            <Gear size={16} className="mr-1.5" /> <span className="hidden sm:inline">Edit </span>campaign
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => setDeleteCampaignOpen(true)} className="border-rose-200 text-rose-600 hover:bg-rose-50 h-9 px-3" data-testid="delete-campaign-btn">
              <Trash size={16} className="mr-1.5 sm:mr-1.5" /> <span className="hidden sm:inline">Delete</span>
            </Button>
          )}
          <Button variant="outline" onClick={() => setCsvOpen(true)} className="border-slate-300 h-9 px-3" data-testid="import-csv-btn">
            <FileArrowUp size={16} className="mr-1.5" /> <span className="hidden sm:inline">Import </span>CSV
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3" data-testid="add-creator-btn">
            <UserPlus size={16} className="mr-1.5" /> <span className="hidden sm:inline">Add </span>creator
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <SummaryStat
          label={isCreatorBased ? "Budget (client + fee)" : "Budget"}
          value={fmtCur(revenueDisplay)}
          hint={isCreatorBased ? `${scopedCount} Locked/Live${agencyFeePct > 0 ? ` · +${agencyFeePct}% fee` : ""}` : undefined}
          testid="stat-budget"
        />
        <SummaryStat label="Creators" value={`${fmtInt(totals.live)}/${fmtInt(denom)}`} />
        <SummaryStat
          label={isCreatorBased ? "Allocated (internal)" : "Allocated spend"}
          value={fmtCur(allocatedDisplay)}
          hint={`${fmtCur(paidDisplay)} paid`}
          testid="stat-allocated"
        />
        <SummaryStat label="Pending payout" value={fmtCur(pendingDisplay)} tone="rose" testid="stat-pending" />
      </div>

      {isCreatorBased && (agencyFeePct > 0 || clientCostTotal > 0) && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="agency-fee-block">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Client cost</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="afe-client">{fmtCur(clientCostTotal)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Agency fee</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="afe-pct">{agencyFeePct}%</span>
            <span className="text-slate-400 mx-1">→</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="afe-amount">{fmtCur(agencyFeeAmount)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Profit</span>
            <span className={`font-mono font-semibold ${profitDisplay >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="afe-profit">{fmtCur(profitDisplay)}</span>
          </div>
        </div>
      )}

      {isCreatorBased && Number(campaign.commercial || 0) > 0 && clientCostTotal > Number(campaign.commercial || 0) && (
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800" data-testid="budget-delta-warning">
          <div className="mt-0.5 w-5 h-5 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-xs font-bold shrink-0">!</div>
          <div className="text-xs sm:text-sm">
            <span className="font-semibold">Client cost exceeds the quoted commercial by </span>
            <span className="font-mono font-semibold">{fmtCur(clientCostTotal - Number(campaign.commercial))}</span>.{" "}
            <span className="text-amber-700">Quoted {fmtCur(campaign.commercial)} · Current client cost {fmtCur(clientCostTotal)}.</span>
          </div>
        </div>
      )}

      {/* Total reach */}
      <ReachStat live={live} />

      {campaign.additional_costs > 0 && (
        <Card className="p-4 border border-slate-200 shadow-none" data-testid="additional-costs-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Legacy additional cost</div>
              <div className="text-xs text-slate-500 mt-0.5">{campaign.additional_costs_note || "Migrate to the new list below"}</div>
            </div>
            <div className="stat-num text-xl font-bold text-slate-900">{fmtCur(campaign.additional_costs)}</div>
          </div>
        </Card>
      )}

      {/* Additional costs list */}
      <Card className="border border-slate-200 shadow-none" data-testid="additional-costs-section">
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Extras</div>
            <div className="font-heading text-base sm:text-lg font-semibold text-slate-900">
              Additional costs
              {addCosts.length > 0 && (
                <span className="text-slate-500 text-xs sm:text-sm font-normal ml-2 block sm:inline mt-0.5 sm:mt-0">
                  · Total {fmtCur(addCostsTotal)} · Paid {fmtCur(addCostsPaid)}
                </span>
              )}
            </div>
          </div>
          <Button onClick={() => { setEditingCost(null); setCostDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white h-9 shrink-0" data-testid="add-cost-btn">
            <Plus size={14} className="mr-1.5" /> <span className="hidden sm:inline">Additional </span>cost
          </Button>
        </div>
        {addCosts.length === 0 ? (
          <div className="p-6 sm:p-8 text-center text-sm text-slate-500">
            No extra costs yet. Click <b>Additional cost</b> to log travel, courier, rentals, etc.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {addCosts.map((x) => (
              <div key={x.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2 sm:gap-3 hover:bg-slate-50/60" data-testid={`cost-row-${x.id}`}>
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <div className="text-sm font-medium text-slate-900">{x.cost_type}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {x.poc_name || "—"}{x.poc_phone ? ` · ${x.poc_phone}` : ""}
                    {x.invoice_file_id && (
                      <> · <button
                        type="button"
                        onClick={() => openInvoice(x.invoice_file_id, x.invoice_filename)}
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`cost-row-invoice-${x.id}`}
                      >Invoice</button></>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0 ml-auto">
                  <PaymentStatusBadge value={x.payment_status} />
                  <div className="text-right">
                    <div className="stat-num text-sm font-semibold text-slate-900">{fmtCur(x.amount)}</div>
                    <div className="text-[11px] text-slate-500">{fmtCur(x.paid_amount)} paid</div>
                  </div>
                  <button onClick={() => setCostRequestFor(x)} className="p-1.5 text-amber-600 hover:text-white hover:bg-amber-600 rounded transition-colors" title="Request payment" data-testid={`request-cost-pay-${x.id}`}>
                    <HandCoins size={14} />
                  </button>
                  <button onClick={() => { setEditingCost(x); setCostDialogOpen(true); }} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-cost-${x.id}`}>
                    <Pencil size={14} />
                  </button>
                  {isAdmin && (
                    <button onClick={() => setCostToDelete(x)} className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`delete-cost-${x.id}`}>
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Script pipeline for locked creators */}
      {(() => {
        const scriptCounts = creators
          .filter((cr) => cr.status === "Locked")
          .reduce((acc, cr) => {
            const s = cr.script_status || "Script Pending";
            acc[s] = (acc[s] || 0) + 1;
            return acc;
          }, {});
        const scriptOrder = [
          "Script Pending", "Script Shared with Brand", "Rework on Script",
          "Script Approved", "Shoot Done", "Video Shared", "Video Rework",
          "Video Approved", "Live",
        ];
        const entries = scriptOrder.filter((s) => scriptCounts[s]).map((s) => [s, scriptCounts[s]]);
        if (entries.length === 0) return null;
        return (
          <Card className="p-4 border border-slate-200 shadow-none" data-testid="script-pipeline-card">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Script pipeline</div>
            <div className="flex flex-wrap gap-2">
              {entries.map(([name, count]) => (
                <div key={name} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
                  <span className="stat-num text-base font-semibold text-slate-900">{count}</span>
                  <span className="text-xs text-slate-600">{name}</span>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Search */}
      <Card className="p-3 flex flex-wrap gap-2 items-center border border-slate-200 shadow-none">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search creators…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="search-creators-input" />
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 w-full sm:max-w-3xl">
          <TabsTrigger value="prospect" data-testid="tab-prospect" className="text-[11px] sm:text-sm">Prospects ({prospects.length})</TabsTrigger>
          <TabsTrigger value="shortlisted" data-testid="tab-shortlisted" className="text-[11px] sm:text-sm">Shortlisted ({shortlisted.length})</TabsTrigger>
          <TabsTrigger value="locked" data-testid="tab-locked" className="text-[11px] sm:text-sm">
            <LockKey size={12} className="mr-1 hidden sm:inline" /> Locked ({locked.length})
          </TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live" className="text-[11px] sm:text-sm">Live ({live.length})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="text-[11px] sm:text-sm">
            <XCircle size={12} className="mr-1 hidden sm:inline" /> Cancelled ({cancelled.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prospect" className="mt-4">
          <ProspectTable list={prospects} onEdit={openEdit} onDelete={setToDelete} onCreatorUpdated={onCreatorUpdated} />
        </TabsContent>
        <TabsContent value="shortlisted" className="mt-4">
          <ProspectTable list={shortlisted} onEdit={openEdit} onDelete={setToDelete} onCreatorUpdated={onCreatorUpdated} />
        </TabsContent>
        <TabsContent value="locked" className="mt-4">
          <LockedTable list={locked} onEdit={openEdit} onDelete={setToDelete} onPay={openPayments} campaignType={campaign?.campaign_type || "Budget Based"} onCreatorUpdated={onCreatorUpdated} />
        </TabsContent>
        <TabsContent value="live" className="mt-4">
          <LiveTable
            list={live}
            onEdit={openEdit}
            onDelete={setToDelete}
            onPay={openPayments}
            onOpenImport={() => setMetricsOpen(true)}
            onRefreshCampaignId={cid}
            campaignType={campaign?.campaign_type || "Budget Based"}
            isAdmin={isAdmin}
            campaignName={campaign?.brand_name || "campaign"}
            onCreatorUpdated={onCreatorUpdated}
            onFullReload={load}
            paymentRequestsByCreator={paymentRequests.reduce((acc, r) => { (acc[r.creator_id] = acc[r.creator_id] || []).push(r); return acc; }, {})}
          />
        </TabsContent>
        <TabsContent value="cancelled" className="mt-4">
          <ProspectTable list={cancelled} onEdit={openEdit} onDelete={setToDelete} onCreatorUpdated={onCreatorUpdated} muted />
        </TabsContent>
      </Tabs>

      <CreatorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        campaignId={cid}
        campaignType={campaign?.campaign_type || "Budget Based"}
        editing={editing}
        onSaved={(saved) => { setEditing(saved); load(); }}
        onOpenPayments={openPayments}
        onOpenRazorpay={openRazorpay}
        isAdmin={isAdmin}
      />
      <RazorpayPayoutDialog open={razorpayOpen} onOpenChange={setRazorpayOpen} creator={razorpayCreator} onSaved={() => load()} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} creator={payCreator} onSaved={() => load()} />
      <CsvImport open={csvOpen} onOpenChange={setCsvOpen} campaignId={cid} onDone={load} />
      <MetricsImport open={metricsOpen} onOpenChange={setMetricsOpen} campaignId={cid} onDone={load} />
      <EditCampaignDialog open={editCampaignOpen} onOpenChange={setEditCampaignOpen} campaign={campaign} onSaved={(saved) => setCampaign(saved)} />
      <CampaignActivityDrawer open={activityOpen} onOpenChange={setActivityOpen} campaignId={cid} />
      <AdditionalCostDialog
        open={costDialogOpen}
        onOpenChange={setCostDialogOpen}
        campaignId={cid}
        editing={editingCost}
        onSaved={(saved) => { setEditingCost(saved); loadAddCosts(); load(); }}
      />

      <RequestCostPaymentDialog
        open={!!costRequestFor}
        onOpenChange={(v) => !v && setCostRequestFor(null)}
        cost={costRequestFor}
        onCreated={() => { setCostRequestFor(null); loadAddCosts(); load(); }}
      />

      <AlertDialog open={!!costToDelete} onOpenChange={(v) => !v && setCostToDelete(null)}>
        <AlertDialogContent data-testid="delete-cost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this cost?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{costToDelete?.cost_type}</b> ({fmtCur(costToDelete?.amount)}){costToDelete?.invoice_file_id ? " and its attached invoice" : ""} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cost">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/additional-costs/${costToDelete.id}`);
                  toast.success("Cost deleted");
                  setCostToDelete(null);
                  loadAddCosts();
                  load();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
                }
              }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-cost"
            >
              Delete cost
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteCampaignOpen} onOpenChange={setDeleteCampaignOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{campaign.brand_name}</b> and every creator, invoice and payment inside it will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/campaigns/${cid}`);
                  toast.success("Campaign deleted");
                  navigate("/campaigns");
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
                }
              }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-campaign"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove @{toDelete?.username}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the creator and all their payment records for this campaign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
